import { useCallback, useState } from 'react';
import {
  faCalendarDay,
  faChartSimple,
  faComment,
  faEye,
  faFileLines,
  faGlobe,
  faLock,
  faThumbsUp,
} from '@fortawesome/pro-regular-svg-icons';
import { faThumbTack } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { queryString } from '@olivierpascal/helpers';
import { Anchored, Stack, Text } from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import type { ITableRowProps } from '~/components/molecules/Table/TableRow';
import type { IApiGetArticleListItemResponseBody } from '~/lib/api/modules/Articles';
import { Thumbnail } from '~/components/atoms/Thumbnail';
import { PublicationStatus } from '~/components/molecules/PublicationStatus';
import { Table } from '~/components/molecules/Table';
import { getFormattedIapProductPrice } from '~/components/organisms/fields/IapProductSelector/getFormattedIapProductPrice';
import { getIapProductById } from '~/components/organisms/fields/IapProductSelector/getIapProductById';
// import { copyToClipboard } from '~/helpers/copyToClipboard';
import { formatEventDate } from '~/helpers/formatEventDate';
import { useArticleLikesViewsQuery } from '~/lib/api/modules/Articles/hooks/useArticleLikesViewsQuery';
import { useDeleteArticleMutation } from '~/lib/api/modules/Articles/hooks/useDeleteArticleMutation';
import { useGenerateArticleShareUrlMutation } from '~/lib/api/modules/Articles/hooks/useGenerateArticleShareUrlMutation';
import { usePictureQuery } from '~/lib/api/modules/Pictures/hooks/usePictureQuery';
import { useVideoQuery } from '~/lib/api/modules/Videos/hooks/useVideoQuery';
import { ArticleListItemCopyShareLinkDialog } from './ArticleListItemCopyShareLinkDialog';
import { ArticleListItemMenu } from './ArticleListItemMenu';

export type IArticleListItemProps = ITableRowProps & {
  article: IApiGetArticleListItemResponseBody;
  parentCategoryId?: string;
};

const styles = stylex.create({
  articleIcon: {
    marginRight: '0.25rem',
  },
  colSpan2: {
    gridColumn: 'span 2',
  },
});

export const ArticleListItem: React.FC<IArticleListItemProps> = (props) => {
  const { article, parentCategoryId, ...other } = props;
  const { t, i18n } = useTranslation();
  const deleteArticleMutation = useDeleteArticleMutation();
  const generateArticleShareUrlMutation = useGenerateArticleShareUrlMutation();
  const likesAndViews = useArticleLikesViewsQuery({
    appId: article.appId,
    articleId: article._id,
  });
  const [shareDialogOpened, setShareDialogOpened] = useState(false);

  const locale = i18n.language;

  const { appId } = article;
  const pictureId = article.pictures?.[0];
  const videoId = !pictureId ? article.videos?.[0] : undefined;

  // TODO: avoid making this request, see tickets APIV1-3 and APIV1-5.
  const pictureQuery = usePictureQuery(
    pictureId ? { appId, pictureId } : undefined,
  );

  // TODO: avoid making this request, see tickets APIV1-3 and APIV1-5.
  const videoQuery = useVideoQuery(videoId ? { appId, videoId } : undefined);

  const lastPublishedArticle = article;
  const lastDraft = article.draft;
  const lastVersion = lastDraft
    ? { ...lastDraft, ...lastPublishedArticle }
    : lastPublishedArticle;

  const likes =
    (lastVersion.likes ?? 0) + (likesAndViews.data?.counters['#total'] ?? 0);
  const views =
    (lastVersion.views ?? 0) + (likesAndViews.data?.counters['#views'] ?? 0);

  const publicationDate = lastVersion?.publicationDate
    ? DateTime.fromISO(lastVersion.publicationDate)
    : undefined;
  const unpublicationDate = lastVersion?.unpublicationDate
    ? DateTime.fromISO(lastVersion.unpublicationDate)
    : undefined;

  const searchParams = queryString.build({ parent: parentCategoryId });
  const qs = searchParams ? `?${searchParams}` : '';
  const actionUrl = `/apps/${article.appId}/articles/${article._id}/update${qs}`;
  const product = article.storeProductId
    ? getIapProductById(article.storeProductId)
    : undefined;
  const articleIcon = lastVersion.isWebview
    ? faGlobe
    : lastVersion.isPoll
      ? faChartSimple
      : lastVersion.isEvent
        ? undefined
        : faFileLines;
  const thumbnailUrl = pictureQuery.data?.thumbUrl ?? videoQuery.data?.thumbUrl;

  const hadBadgeOrProduct = !!lastVersion.badges?.list.length || !!product;
  const processing =
    deleteArticleMutation.isLoading ||
    generateArticleShareUrlMutation.isLoading;

  const handleDelete = (): void => {
    // eslint-disable-next-line no-alert
    const doDeletion = confirm(
      t('ArticleListItem.dialog.confirmDeletion.text', {
        title: article.title,
      }),
    );

    if (doDeletion) {
      void deleteArticleMutation
        .mutateAsync({
          appId: article.appId,
          articleId: article._id,
        })
        .catch(() => {
          // TODO: handle error (snackbar)
        });
    }
  };

  const handleCopyShareUrl = (): Promise<unknown> => {
    setShareDialogOpened(true);
    return Promise.resolve(true);
  };

  const getShareUrl = useCallback(async (): Promise<string | null> => {
    if (article.shareUrl) {
      return article.shareUrl;
    } else {
      const newArticle = await generateArticleShareUrlMutation.mutateAsync({
        appId: article.appId,
        articleId: article._id,
      });
      if (!newArticle.shareUrl) {
        return null;
      }
      return newArticle.shareUrl;
    }
  }, [
    article._id,
    article.appId,
    article.shareUrl,
    generateArticleShareUrlMutation,
  ]);

  return (
    <Table.Row {...other}>
      <Stack
        as={Table.BodyAction}
        horizontal
        gap={3}
        href={actionUrl}
        sx={hadBadgeOrProduct ? undefined : styles.colSpan2}
      >
        <Anchored
          content={
            lastVersion.pinned ? (
              <FontAwesomeIcon icon={faThumbTack} size="lg" />
            ) : undefined
          }
          horizontalOrigin="right"
          verticalOrigin="bottom"
        >
          <Thumbnail
            url={thumbnailUrl}
            loading={pictureQuery.isLoading}
            size="lg"
          />
        </Anchored>

        <Stack gap={1}>
          <Text variant="title" size="sm" truncateLines={2}>
            {articleIcon ? (
              <FontAwesomeIcon
                {...stylex.props(styles.articleIcon)}
                icon={articleIcon}
              />
            ) : null}
            {lastVersion.title}
          </Text>

          <Stack horizontal gap={2}>
            {lastVersion.isEvent &&
            lastVersion.eventStartDate &&
            lastVersion.eventEndDate ? (
              <Stack horizontal gap={1}>
                <FontAwesomeIcon icon={faCalendarDay} />
                <Text truncateLines={1}>
                  {formatEventDate(
                    lastVersion.eventStartDate,
                    lastVersion.eventEndDate,
                    t,
                    locale,
                  )}
                </Text>
              </Stack>
            ) : lastVersion.createdAt ? (
              DateTime.fromISO(lastVersion.createdAt).toLocaleString(
                DateTime.DATE_FULL,
                { locale },
              )
            ) : null}
            <Stack horizontal gap={1}>
              <FontAwesomeIcon icon={faThumbsUp} />
              {String(likes ?? 0)}
            </Stack>
            <Stack horizontal gap={1}>
              <FontAwesomeIcon icon={faEye} />
              {String(views ?? 0)}
            </Stack>
            <Stack horizontal gap={1}>
              <FontAwesomeIcon icon={faComment} />
              {String(lastVersion.commentsCount ?? 0)}
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      {hadBadgeOrProduct ? (
        <Stack as={Table.Body} horizontal gap={2}>
          {lastVersion.badges?.list.length ? (
            <FontAwesomeIcon icon={faLock} size="lg" />
          ) : null}
          {product ? (
            <div>{getFormattedIapProductPrice(product.customerPrice)}</div>
          ) : null}
        </Stack>
      ) : null}

      <Table.Body>
        <PublicationStatus
          published={article.isPublished}
          publicationDate={publicationDate}
          unpublicationDate={unpublicationDate}
        />
      </Table.Body>

      <Stack as={Table.Body} horizontal justify="end">
        <ArticleListItemMenu
          appId={article.appId}
          articleId={article._id}
          handleDelete={handleDelete}
          handleCopyShareUrl={handleCopyShareUrl}
          processing={processing}
        />
      </Stack>

      <ArticleListItemCopyShareLinkDialog
        opened={shareDialogOpened}
        onClose={() => setShareDialogOpened(false)}
        getShareUrl={getShareUrl}
      />
    </Table.Row>
  );
};
